import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import tradesData from '../content/tradesData';

// Hero Section Component for Trade-Specific Page
const TradeHeroSection = ({ trade }) => {
  return (
    <section className="relative min-h-[80vh] flex items-center justify-center overflow-hidden bg-gradient-to-br from-[#111827] to-[#1F2937]">
      <div className="absolute inset-0 overflow-hidden">
        <svg className="absolute inset-0 w-full h-full">
          <defs>
            <pattern id="grid" width="40" height="40" patternUnits="userSpaceOnUse">
              <path d="M 40 0 L 0 0 0 40" fill="none" stroke="rgba(255,255,255,0.03)" strokeWidth="1"/>
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#grid)" />
        </svg>
      </div>
      
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center">
        <motion.h1
          className="text-4xl sm:text-5xl md:text-6xl font-bold text-white mb-6"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          {trade.h1Heading}
        </motion.h1>
        
        <motion.p
          className="text-xl text-gray-300 mb-8 max-w-3xl mx-auto"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          {trade.introParagraph}
        </motion.p>
        
        <motion.div
          className="flex flex-col sm:flex-row gap-4 justify-center items-center"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
        >
          <Link to="/register">
            <button className="px-8 py-3 bg-[#4F46E5] text-white rounded-lg font-semibold hover:bg-[#4338CA] transition-colors">
              Get Started for Free
            </button>
          </Link>
          <Link to="/login" className="text-gray-300 hover:text-white transition-colors">
            Already have an account? Log In
          </Link>
        </motion.div>
      </div>
    </section>
  );
};

// Features Section for Trade-Specific Page
const TradeFeaturesSection = ({ trade }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.3 });

  return (
    <section ref={ref} className="py-20 bg-[#F9FAFB]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          className="text-center mb-12"
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
        >
          <h2 className="text-3xl font-bold text-[#111827] mb-4">Why {trade.tradeSlug.charAt(0).toUpperCase() + trade.tradeSlug.slice(1)} Choose SwiftBidPro</h2>
          <p className="text-lg text-[#111827] opacity-70">Tailored tools for your trade</p>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-8">
          {trade.tradeSpecificFeatures.map((feature, index) => (
            <motion.div
              key={index}
              className="bg-white p-6 rounded-lg shadow-sm border border-gray-200"
              initial={{ opacity: 0, y: 20 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: index * 0.1 }}
            >
              <h3 className="text-xl font-bold text-[#111827] mb-3">{feature.title}</h3>
              <p className="text-[#111827] opacity-70">{feature.description}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// CTA Section for Trade-Specific Page
const TradeCTASection = ({ trade }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.5 });

  return (
    <section ref={ref} className="py-20 bg-[#4F46E5]">
      <motion.div
        className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8"
        initial={{ opacity: 0, y: 20 }}
        animate={inView ? { opacity: 1, y: 0 } : {}}
        transition={{ duration: 0.6 }}
      >
        <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
          Ready to Transform Your {trade.tradeSlug.charAt(0).toUpperCase() + trade.tradeSlug.slice(1)} Business?
        </h2>
        <p className="text-xl text-white/90 mb-8">
          Join thousands of professionals who've streamlined their quoting process
        </p>
        <Link to="/register">
          <button className="px-8 py-3 bg-white text-[#4F46E5] rounded-lg font-semibold hover:bg-gray-100 transition-colors">
            Create Your First Quote for Free
          </button>
        </Link>
      </motion.div>
    </section>
  );
};

// Footer Component (reused from HomePage)
const Footer = () => {
  return (
    <footer className="bg-[#111827] text-white py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col md:flex-row justify-between items-center">
          <div className="mb-4 md:mb-0">
            <span className="text-2xl font-bold">SwiftBid Pro</span>
          </div>
          <div className="flex space-x-6 text-sm">
            <Link to="/privacy-policy" className="hover:text-[#4F46E5] transition-colors">Privacy Policy</Link>
            <Link to="/terms-of-service" className="hover:text-[#4F46E5] transition-colors">Terms of Service</Link>
            <a href="#" className="hover:text-[#4F46E5] transition-colors">Contact</a>
          </div>
        </div>
        <div className="mt-8 text-center text-sm text-gray-400">
          © 2025 SwiftBid Pro. All rights reserved.
        </div>
      </div>
    </footer>
  );
};

// Main Trade-Specific Landing Page Component
const TradeSpecificLandingPage = () => {
  const { tradeSlug } = useParams();
  const trade = tradesData.find(t => t.tradeSlug === tradeSlug);

  if (!trade) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F9FAFB]">
        <div className="text-center">
          <h1 className="text-3xl font-bold text-[#111827] mb-4">Trade Not Found</h1>
          <p className="text-[#111827] opacity-70 mb-6">We couldn't find the trade you're looking for.</p>
          <Link to="/">
            <button className="px-6 py-2 bg-[#4F46E5] text-white rounded-lg font-semibold hover:bg-[#4338CA] transition-colors">
              Back to Home
            </button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <Helmet>
        <title>{trade.pageTitle}</title>
        <meta name="description" content={trade.metaDescription} />
        <meta name="keywords" content={`${trade.primaryKeyword}, ${trade.secondaryKeywords.join(', ')}`} />
        <script type="application/ld+json">
          {`
            {
              "@context": "https://schema.org",
              "@type": "SoftwareApplication",
              "name": "SwiftBidPro",
              "applicationCategory": "BusinessApplication",
              "description": "${trade.metaDescription}",
              "operatingSystem": "Web",
              "offers": {
                "@type": "Offer",
                "price": "0",
                "priceCurrency": "USD"
              }
            }
          `}
        </script>
      </Helmet>
      <TradeHeroSection trade={trade} />
      <TradeFeaturesSection trade={trade} />
      <TradeCTASection trade={trade} />
      <Footer />
    </div>
  );
};

export default TradeSpecificLandingPage;
